// The sign-in and session calls to our own server (docs/plans/sign-in.md, API routes).
// Every POST sends JSON, because the server refuses state-changing requests that are not JSON.
import type { RenewalPremiumSource } from '@safeside/shared';
import { roleLabels, type Role, type RoleCode } from '../roles';

/** Who a renewal can go to (Reassign): the people, and whether this person may use the pool or take it. */
export type Assignees = { people: Assignee[]; canUnassign: boolean; canTakeOwn: boolean };

export type SignedInUser = {
  id: string;
  name: string;
  initials: string;
  role: Role;
  email: string;
};

export type Me = {
  user: SignedInUser;
  sessionEndsAt: string;
  deviceRememberedUntil: string | null;
};

/** What GET /api/me says about this browser. */
export type MeResult =
  { kind: 'signed_in'; me: Me } | { kind: 'signed_out' } | { kind: 'expired'; email: string };

/** A failed call: the server's error code, or "network" when the server could not be reached. */
export type Failure = { ok: false; status: number; error: string; body: Record<string, unknown> };
export type Result<T> = { ok: true; body: T } | Failure;

/** After the password step: "code" comes with the seconds before Resend is offered. */
export type SignInNext = { next: 'code' | 'done'; resendIn?: number };

export type LinkInfo = { kind: 'invitation' | 'password_reset'; email: string };

export type UserStatus = 'invited' | 'active' | 'out_today' | 'inactive';

/** One row of Settings, Users. `role` is the stored code; roleLabels gives the name. */
export type UserRow = {
  id: string;
  name: string;
  email: string;
  role: RoleCode;
  initials: string;
  status: UserStatus;
  lastSignInAt: string | null;
};

export type UserChanges = Partial<{
  name: string;
  initials: string;
  role: RoleCode;
  outToday: boolean;
  status: 'active' | 'inactive';
}>;

export type NewUser = { email: string; name: string; initials: string; role: RoleCode };

/** Sign-in code rules (spec C1), changed by the Admin in Settings, Integrations. */
export type CodeRules = { expiryMinutes: number; resendSeconds: number; maxWrongTries: number };

/** One changed field in one save, newest first. */
export type SettingChange = {
  at: string;
  by: string | null;
  field: string;
  before: unknown;
  after: unknown;
};

/** One row of Settings, Activity log (spec C15). */
export type ActivityType = 'Sign-in' | 'Settings change';
export type ActivityEntry = {
  id: string;
  at: string;
  who: string;
  whoId: string | null;
  whoRole: RoleCode | null;
  type: ActivityType;
  action: string;
  before: string;
  after: string;
};

/** The Activity log for a New York date range (YYYY-MM-DD), with everyone for the Person filter. */
export type ActivityView = {
  from: string;
  to: string;
  /** Today in New York, by the server's clock. */
  today: string;
  entries: ActivityEntry[];
  /** The range has more actions than one page reads; the newest are shown. */
  truncated: boolean;
  people: { id: string; name: string; role: RoleCode }[];
};

/** One line of a person's History in the user editor. */
export type HistoryLine = { at: string; by: string; field: string; before: string; after: string };

export type CodeRulesView = {
  rules: CodeRules;
  defaults: CodeRules;
  bounds: Record<keyof CodeRules, { min: number; max: number }>;
  history: SettingChange[];
};

/** A renewal's status code (spec C4); renewal-lines.ts has the labels, stages and colors. */
export type RenewalStatus =
  | 'pending'
  | 'requested'
  | 'need_payment'
  | 'quote'
  | 'increase'
  | 'non_renewal'
  | 'noc'
  | 'done'
  | 'cancelled';

/** One row of My renewals or All renewals, as GET /api/renewals sends it. */
export type RenewalRow = {
  id: string;
  insured: string;
  insuredFull: string;
  policyNumber: string;
  line: string;
  carrier: string | null;
  expirationDate: string;
  daysLeft: number;
  status: RenewalStatus;
  handledBy: { id: string; name: string; initials: string } | null;
  priorPremium: number | null;
  /** The VA's correction, else the dec page's reading, else AMS360's (S14, one rule everywhere). */
  renewalPremium: number | null;
  renewalPremiumSource: RenewalPremiumSource | null;
  /** AMS360's renewed premium alone, so the Record can apply a correction made on the page. */
  amsRenewalPremium: number | null;
  agencyBilled: boolean;
  renewedInAmsAt: string | null;
  inactiveInAmsSince: string | null;
  /** Auto fetch, a fetch going now ("Fetching"), and fetch trouble that needs attention (C5). */
  fetch: RowFetch;
  /** Open in AMS360 (spec C13): the customer and the policy's dec page. Links only. */
  ams: { customer: string; decPage: string };
  /** The step the renewal is on (spec C4 step sub-filter); null once every step is done. */
  currentStep: CurrentStep | null;
  /** The renewal has a note: the note icon in the Insured column (spec C7.3). */
  hasNote: boolean;
  /** Recheck tomorrow's day while step 2 waits for AMS360's download (S16); null otherwise. */
  recheck?: string | null;
  /**
   * Closed renewals only (Done or Cancelled): when, by whom (null for the System) and how (S16).
   * Missing from a server from before, read as not known.
   */
  closed?: { at: string; by: string | null; outcome: ClosedOutcome } | null;
  /**
   * The open handoff (S15): waiting for the AE's answer (Awaiting AE) or accepted (With AE). Missing
   * from a server from before, read as none.
   */
  handoff?: OpenHandoff | null;
  /**
   * The branch that changes the checklist (spec C8, S17): the yellow Non-renewal tag with its
   * effective date, or the grey Policy inactive tag. Missing from a server from before, read as none.
   */
  branch?: { kind: RenewalBranch; effectiveDate: string } | null;
  /**
   * The AE's My renewals only (S15 batch 4): the latest handoff to them that ended this week, for the
   * Finished tab. Missing from a server from before, read as none.
   */
  finishedHandoff?: FinishedHandoff | null;
  /**
   * The AE's answer to the Increase tag (S14, part 1 of the increase review). No answer button
   * exists yet, so `reviewedBy` and `reviewedAt` are always null today.
   */
  increaseReview: {
    state: IncreaseReviewState;
    reviewedBy: { id: string; name: string; initials: string } | null;
    reviewedAt: string | null;
  };
};

/** Why a renewal goes to an AE (spec C6, Hand to AE). */
export type HandoffReason =
  | 'quote'
  | 'increase'
  | 'non_renewal'
  | 'noc'
  | 'payment_not_received'
  | 'not_on_carrier_site'
  | 'other';
export type HandoffKind = 'handoff' | 'reassign_to_ae' | 'take_over';
type Person = { id: string; name: string; initials: string };
/** A renewal's open handoff, as the lists and the Record carry it (apps/server/src/open-handoffs.ts). */
export type OpenHandoff = {
  id: string;
  kind: HandoffKind;
  state: 'waiting' | 'accepted';
  reason: HandoffReason | null;
  reassignReason: ReassignReason | null;
  otherText: string | null;
  note: string | null;
  /** Who the work returns to; null for the Unassigned pool. */
  from: Person | null;
  to: Person;
  createdBy: Person;
  createdAt: string;
  answeredAt: string | null;
  /** New York calendar day (YYYY-MM-DD); null for a take over. */
  actBy: string | null;
  /** The AE's Follow up on day (YYYY-MM-DD); missing from a server from before, read as none. */
  followUpOn?: string | null;
};
/** How a handoff to an AE ended (spec C6, the AE's Finished tab; apps/server/src/open-handoffs.ts). */
export type FinishedHandoff = {
  id: string;
  kind: HandoffKind;
  state: 'sent_back' | 'passed' | 'returned' | 'closed' | 'cancelled';
  reason: HandoffReason | null;
  reassignReason: ReassignReason | null;
  otherText: string | null;
  outcome: 'renewed' | 'rewritten' | 'lost' | 'not_renewed' | null;
  continueAtStep: string | null;
  from: Person | null;
  /** Who sent it to the AE. */
  createdBy: Person;
  createdAt: string;
  finishedAt: string;
  finishedBy: Person | null;
  /** A pass: the AE it went to next. */
  passedTo: Person | null;
  /**
   * Cancelled because the renewal moved (S15 batch 7): reassigned (null `to`: the Unassigned pool)
   * or taken over by another AE. Missing from a server from before, read as none.
   */
  endedBy?: { why: 'reassigned' | 'taken_over'; to: Person | null } | null;
};
/** The AE's note after a send back or a return, for the top of the VA's Record (spec C6). */
export type BackFromAe = {
  handoffId: string;
  state: 'sent_back' | 'returned';
  note: string;
  by: Person | null;
  at: string;
  continueAtStep: ContinueAtStep | null;
  /**
   * Rewritten with another carrier and returned for the bank step (S15 batch 6c): the new policy
   * the VA sends to the bank. Missing from a server from before, read as none.
   */
  rewrite?: { carrier: string; premium: number; policyNumber: string } | null;
};
/** Lost's reasons in Close it myself (spec C6). */
export type LostReason = 'price' | 'coverage' | 'service' | 'no_response' | 'other';
/**
 * Finish handoff, Close it myself (spec C6): how it ended, with that outcome's details. A rewrite
 * with `bank` goes back to the VA at Proof to bank once the new dec page is uploaded.
 */
export type HandoffClose =
  | { outcome: 'renewed' }
  | {
      outcome: 'rewritten';
      carrier: string;
      premium: number;
      policyNumber: string;
      bank: boolean;
      /** The bank step's new dec page, as Upload PDF answered; set by the dialog after the upload. */
      decPageId?: string;
    }
  | { outcome: 'lost'; lostReason: LostReason; wentTo: string | null }
  | { outcome: 'not_renewed'; reason: string };
/** Hand to AE, or Reassign to an AE: `from` is who handles it as the page saw it. */
export type HandoffCreate =
  | {
      kind: 'handoff';
      reason: HandoffReason;
      otherText?: string;
      note?: string;
      to: string;
      from: string | null;
    }
  | {
      kind: 'reassign_to_ae';
      reassignReason: ReassignReason;
      note?: string;
      to: string;
      from: string | null;
    }
  /** Take over (decision 1, Nov 1): no reason, the status stays; `from` as the page saw it. */
  | { kind: 'take_over'; from: string | null };
export type HandoffAnswer = {
  handoff: { id: string; state: string };
  /** A pass: the next AE's handoff, waiting for their answer (its cancel is the pass's Undo). */
  next?: { id: string; state: string };
  renewal: { handledBy: string | null; status: RenewalStatus };
};
/** The handoff after Follow up on or its Undo, and when a Follow up on was made (for its Undo). */
export type FollowUpAnswer = {
  handoff: { id: string; followUpOn: string | null };
  change?: { at: string };
};

/** How a closed renewal ended (the Closed tab's Outcome filter; S16). */
export type ClosedOutcome =
  'renewed' | 'rewritten' | 'lost' | 'nonrenewed' | 'paid' | 'cancelled' | 'sheet' | 'inactive';

/** The AE's answer to an Increase tag: none until the AE on the policy looks it over. */
export type IncreaseReviewState = 'none' | 'looked_over' | 'reshop';

/** "dec" is step 1 Dec page. */
export type CurrentStep = 'dec' | StepKey;

/** Someone a renewal can be reassigned to (spec C6): active Renewal VAs and Account Executives. */
export type Assignee = {
  id: string;
  name: string;
  role: 'renewal_va' | 'account_executive';
  outToday: boolean;
};
export type ReassignReason = 'workload' | 'out_of_office' | 'carrier_specialty' | 'other';
/** One renewal to move: who handles it now (as the page saw it) and who gets it; null is Unassigned. */
export type ReassignItem = { id: string; from: string | null; to: string | null };
export type ReassignResult = {
  moved: string[];
  skipped: { id: string; why: 'not_found' | 'closed' | 'not_yours' | 'changed' | 'same' }[];
};

/** One line of the Record's Portal log (spec C7.4, C15). */
export type LogEntry = { at: string; who: string; text: string };

/** A note on a renewal (spec C7.4, S14): append only, newest first. */
export type Note = {
  id: string;
  text: string;
  at: string;
  by: { id: string; name: string; initials: string };
};

/** A renewal's notes; readOnly once the renewal is deleted. */
export type NotesView = { notes: Note[]; readOnly: boolean };

export type FetchBlock = 'failed_twice' | 'login_rejected';

export type RowFetch = {
  auto: boolean;
  running: boolean;
  /** The queued fetch waits for its carrier login, busy with another fetch. */
  waiting: boolean;
  attention: FetchBlock | null;
};

export type FetchStage =
  | 'queued'
  | 'waiting_for_carrier'
  | 'logging_in'
  | 'waiting_for_code'
  | 'opening_policy'
  | 'downloading'
  | 'done';

export type FetchErrorKind =
  | 'no_code'
  | 'login_rejected'
  | 'not_available'
  | 'not_found'
  | 'site_changed'
  | 'download_failed'
  | 'timeout'
  | 'error'
  | 'site_down'
  | 'not_configured'
  | 'no_matching_document';

/** The Dec page step's fetch, as GET /api/renewals/:id/fetch sends it (spec C11). */
export type FetchView = {
  now: string;
  carrier: { key: string; name: string; siteUrl: string } | null;
  /** Off while the renewal is closed, for everyone. */
  canWork: boolean;
  /** The renewal is closed (Done or Cancelled): read only until the Manager reopens it (spec C16). */
  closed: boolean;
  canFetch: boolean;
  /** The carrier's site was down: fetches wait until then (15 minutes). Null when no wait. */
  carrierRestingUntil: string | null;
  blocked: FetchBlock | null;
  job: {
    id: string;
    status: 'queued' | 'running' | 'ok' | 'failed';
    stage: FetchStage;
    errorKind: FetchErrorKind | null;
    errorRef: string | null;
    createdAt: string;
    startedAt: string | null;
    stageAt: string;
    finishedAt: string | null;
    startedBy: string | null;
    queuePosition: number | null;
    carrierBusy: boolean;
    notAvailable: boolean;
    nonRenewal: boolean;
    /** The carrier's site was down when this job ran: Fetch stays open (not an attempt). */
    siteDown: boolean;
    /** A failed job's last page, the Admin only. */
    screenshotId: string | null;
  } | null;
  failures: {
    jobId: string;
    at: string;
    errorKind: FetchErrorKind | null;
    errorRef: string | null;
    /** The Admin only. */
    screenshotId: string | null;
  }[];
  documents: {
    id: string;
    kind: DocumentKind;
    fileName: string;
    size: number;
    termEffectiveDate: string | null;
    source: 'fetch' | 'upload' | 'email';
    createdAt: string;
    by: string | null;
    fetchSeconds: number | null;
  }[];
  /** Files added from the Files card (S14), newest first. */
  files: AddedFile[];
  /** The reading of the renewal dec page (spec C10), or null when there is none yet. */
  reading: Reading | null;
  /** Not on the carrier site yet: from `days` before the renewal date (Settings, Defaults) call the carrier. */
  followUp: { days: number; daysLeft: number; due: boolean };
  /** When the carrier's site usually shows renewals (days before), and whether "not yet" is expected. */
  carrierShows: { days: number; expected: boolean } | null;
};

/** Why a value read from the dec page is marked "Check" (spec C10). */
export type CheckReason =
  | 'low_confidence'
  | 'not_found'
  | 'policy_number_differs'
  | 'term_start_differs'
  | 'term_length'
  | 'premium_change'
  | 'different_insured'
  | 'not_a_dec_page'
  | 'non_renewal';

export type ReadingErrorKind =
  | 'not_readable'
  | 'too_large'
  | 'incomplete'
  | 'service_error'
  | 'timeout'
  | 'not_configured'
  | 'error';

/** The renewal dec page values the VA can confirm or correct (spec C10). */
export type ReadingFixField =
  'namedInsured' | 'policyNumber' | 'effectiveDate' | 'expirationDate' | 'totalPremium';

/** One coverage row as read from a dec page. */
export type Coverage = { name: string; limit: string; deductible: string };

/** A value the VA can confirm or correct: as read, and as the portal uses it. */
export type ReadingField = {
  field: ReadingFixField;
  read: string | number | null;
  value: string | number | null;
  confidence: 'high' | 'medium' | 'low' | null;
  check: CheckReason | null;
  fix: { action: 'confirmed' | 'corrected'; by: string | null; at: string } | null;
};

/** What was read from the renewal dec page. */
export type Reading = {
  status: 'queued' | 'running' | 'done' | 'failed';
  documentId: string;
  errorKind: ReadingErrorKind | null;
  errorRef: string | null;
  finishedAt: string | null;
  /** This person may confirm and correct the values. */
  canFix: boolean;
  fields: ReadingField[];
  coverages: Coverage[];
  /** What was read from the prior term's dec page, for the coverage compare. */
  prior: {
    status: 'queued' | 'running' | 'done' | 'failed';
    totalPremium: number | null;
    coverages: Coverage[];
  } | null;
  values: {
    documentType: string;
    namedInsured: string | null;
    policyNumber: string | null;
    effectiveDate: string | null;
    expirationDate: string | null;
    totalPremium: number | null;
    line: string | null;
    vehicles: number;
    /** Each vehicle (auto): year make model, and the VIN's last 4 when read (S16). */
    vehicleList?: { name: string; vinEnd: string | null }[];
    mortgagees: number;
  } | null;
  /** The checks still open (a confirmed or corrected value is no longer here). */
  checks: { field: string; reason: CheckReason }[];
};

/** Confirm the value read, or correct it (spec C10). */
export type ReadingFix = {
  documentId: string;
  field: ReadingFixField;
  action: 'confirm' | 'correct';
  value?: string;
};

export type EmailPayment = 'escrowed' | 'not_escrowed' | 'due_to_safeside';

/** The Email insured step (spec C9), as GET /api/renewals/:id/email sends it. */
export type EmailDraft = {
  templates: { key: string; name: string; usedFor: string }[];
  template: string;
  suggested: string;
  payment: EmailPayment;
  paymentSuggested: EmailPayment;
  showsPayment: boolean;
  to: string;
  cc: string;
  subject: string;
  body: string;
  /** Values the portal does not know, shown as [Label] in the email. */
  missing: string[];
  /** The template's own [ ] for the VA to fill in. */
  fillIns: number;
  attachments: { id: string; fileName: string; kind: string }[];
  attachmentNote: string | null;
  sent: { id: string; at: string; by: string | null; templateKey: string; subject: string }[];
  /** This person works the record and may mark the email sent. */
  canSend: boolean;
};

export type EmailToSend = {
  templateKey: string;
  to: string;
  cc: string;
  subject: string;
  body: string;
};

/** One email template in Settings (spec C14). */
export type EmailTemplateRow = {
  key: string;
  name: string;
  usedFor: string;
  to: string;
  cc: string;
  subject: string;
  body: string;
  isDefault: boolean;
  updatedAt: string | null;
};

export type TemplatesView = {
  templates: EmailTemplateRow[];
  placeholders: { key: string; label: string }[];
  flags: { key: string; label: string }[];
  canEdit: boolean;
  limits: { recipients: number; subject: number; body: number };
};

export type TemplatePreview = {
  preview: { to: string; cc: string; subject: string; body: string; missing: string[] };
  unknown: string[];
};

/** What a file on a renewal is. */
export type DocumentKind =
  'dec_page' | 'prior_dec_page' | 'id_cards' | 'invoice' | 'other' | 'notice';

/** What Upload PDF may say a file is (spec C8). */
export type UploadKind = 'dec_page' | 'id_cards' | 'invoice' | 'other' | 'notice';

/** The answer to Upload PDF: the file, whether it was already on the record, and the step. */
export type Uploaded = { documentId: string; duplicate: boolean; fetch: FetchView };

/** The biggest PDF Upload PDF takes (the server says the same). */
export const maxUploadBytes = 20 * 1024 * 1024;

/** What a file added from the Files card is (Record board, S14). */
export type FileType = 'dec_page' | 'id_cards' | 'change' | 'other';

/** A file added from the Files card: the only files the card can delete. */
export type AddedFile = {
  id: string;
  fileName: string;
  size: number;
  type: FileType;
  createdAt: string;
  by: string | null;
};

/** The answer to the Files card's Upload PDF: 201 added, 200 already on the record. */
export type FileAdded = {
  duplicate: boolean;
  file?: { id: string; fileName: string; type: FileType };
};

/** One of my fetches that finished, for the message on any page. */
export type FinishedFetch = {
  jobId: string;
  renewalId: string;
  insured: string;
  status: 'ok' | 'failed';
  notAvailable: boolean;
  /** The carrier's site was down: nothing was tried on the renewal. */
  siteDown: boolean;
  /** The worker is not set up to fetch from this carrier here: nothing was tried. */
  notConfigured: boolean;
  finishedAt: string;
};

export type MyFetches = { now: string; open: number; finished: FinishedFetch[] };

export type SyncInfo = {
  lastOkAt: string | null;
  last: {
    status: 'running' | 'ok' | 'failed';
    startedAt: string;
    finishedAt: string | null;
    errorRef: string | null;
  } | null;
};

/** The increase rule as stored in Settings, Defaults (spec C14), read by the server on every request. */
export type IncreaseRule = { percent: number; minDollars: number };

/** Settings, Defaults: the stored rule, and whether this person may change it (the Admin). */
export type IncreaseRuleView = { rule: IncreaseRule; canEdit: boolean };

/** Settings, Defaults: the follow-up days when a renewal is not on the carrier site (PR 211). */
export type FollowUpView = { days: number; canEdit: boolean };

export type RenewalsView = { renewals: RenewalRow[]; sync: SyncInfo; increaseRule: IncreaseRule };

/** One renewal for the read-only Record (GET /api/renewals/:id). */
export type RenewalDetail = RenewalRow & {
  contact: {
    email: string | null;
    phoneHome: string | null;
    phoneCell: string | null;
    phoneBusiness: string | null;
    addressLine1: string | null;
    addressLine2: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
  } | null;
  policy: {
    customerId: string;
    policyId: string;
    carrierCode: string | null;
    writingCompanyCode: string | null;
    priorEffectiveDate: string | null;
    billMethod: string | null;
    departmentCode: string | null;
    executiveCode: string | null;
    repCode: string | null;
    amsStatus: string | null;
    renewalPolicyId: string | null;
    lastSeenAt: string;
  };
  /** Closed from Rachy's renewal sheet by the System: the Record header shows "From sheet". */
  fromSheet: boolean;
  /** The AE's note after a send back or a return; missing from a server from before, read as none. */
  backFromAe?: BackFromAe | null;
};

export type RenewalView = { renewal: RenewalDetail; sync: SyncInfo; increaseRule: IncreaseRule };

/** The record steps after the dec page (spec C8), as the server names them. */
export type StepKey = 'ams360' | 'email' | 'activity' | 'bank' | 'done';
/** Where the VA picks up after a return (Finish handoff): step 1 Dec page, or one of steps 2 to 6. */
export type ContinueAtStep = 'dec_page' | StepKey;
export type StepState = 'done' | 'waiting' | 'open' | 'locked' | 'skipped';

export type StepView = {
  state: StepState;
  /** `byId` is null for a step the System did (a renewal closed from Rachy's sheet). */
  stamp: { by: string | null; byId: string | null; at: string } | null;
  details: Record<string, unknown>;
};

/** How the proof went to the bank (spec C8 A5 Outcome). */
export type BankOutcome = 'mycoverageinfo' | 'email' | 'fax' | 'loan_not_found' | 'not_needed';

/** The branches that change the checklist (spec C8, S17). */
export type RenewalBranch = 'non_renewal' | 'inactive';
/** Why the carrier is not renewing (spec C8). */
export type BranchReason = 'carrier_termination' | 'non_compliance' | 'non_compliance_second';
/** A branch step: stored ones, and Non-renewal step 4 Hand to AE, which is the handoff itself. */
export type BranchStepKey =
  | 'nr_notice'
  | 'nr_email'
  | 'nr_activity'
  | 'nr_hand'
  | 'pi_notice'
  | 'pi_email'
  | 'pi_activity'
  | 'pi_close';

/** The branch the renewal is on, with its four steps (apps/server/src/steps/branch.ts). */
export type BranchView = {
  branch: RenewalBranch;
  reason: BranchReason | null;
  effectiveDate: string;
  setAt: string;
  setBy: { id: string; name: string } | null;
  steps: (StepView & { key: BranchStepKey })[];
  /** The last step ticked with a row: the only one that can be unticked. */
  last: BranchStepKey | null;
  canUndo: boolean;
  /** The template keys step 2 sends, and the ones marked sent since the switch. */
  emails: string[];
  sent: string[];
  notices: { id: string; fileName: string }[];
  activity: { description: string; policyNumber: string };
  sheetComment: string;
  hand: { id: string; to: string | null; state: string } | null;
  handoffOpen: boolean;
};

/** Ticking a branch step: what each needs (spec C8). */
export type BranchTick =
  | { step: 'nr_notice' | 'pi_notice'; documentId: string }
  | { step: 'pi_notice'; none: true }
  | { step: 'nr_email' | 'pi_email' }
  | { step: 'nr_activity' | 'pi_activity'; emailDraggedIn: true }
  | { step: 'pi_close'; sheetComment: string };

/** The record steps 2 to 6 for one renewal (GET /api/renewals/:id/steps). */
export type StepsView = {
  now: string;
  /**
   * The branch the renewal is on (S17): its steps show instead of steps 1 to 6. Missing from a
   * server from before, read as none.
   */
  branch?: BranchView | null;
  canWork: boolean;
  amsStep: 'check' | 'renew';
  /**
   * AMS360 has no new term yet (S16 Batch 3): Check AMS360 waits ("Not downloaded yet"), the day set
   * with Recheck tomorrow, Renew manually instead chosen, Flood/Velocity/Swyfft never downloading.
   * Missing from a server from before, read as not waiting.
   */
  download?: { waiting: boolean; recheck: string | null; manual: boolean; neverDownloads: boolean };
  decPage: {
    attached: boolean;
    reading: 'queued' | 'running' | 'done' | 'failed' | null;
    /** The file on step 1 was read as a non-renewal notice, or as another kind of file. */
    notDecPage: 'non_renewal' | 'other' | null;
    /** Auto: whether the ID cards are on the record too; null for other lines. */
    idCards: boolean | null;
    /**
     * Step 1 marked done previously (S16): who and the day (YYYY-MM-DD). Missing from a server
     * from before, read as not marked.
     */
    previously?: { by: string | null; byId: string; date: string } | null;
  };
  steps: Record<StepKey, StepView>;
  last: StepKey | null;
  canUndo: boolean;
  links: { customer: string; decPage: string };
  premiums: { ams: number | null; dec: number | null; match: boolean | null };
  openChecks: ReadingFixField[];
  renew: {
    policyNumber: string | null;
    priorPolicyNumber: string;
    /** The carrier changes the policy number each term (SageSure). */
    renumbers: boolean;
    premium: number | null;
    effectiveDate: string | null;
    expirationDate: string | null;
    executiveCode: string | null;
    repCode: string | null;
    coverages: Coverage[];
  };
  activity: { description: string; policyNumber: string };
  bank: {
    mortgagees: { name: string; loanNumber: string; clause: string }[];
    agencyBilled: boolean;
    /** The dec page could not be read: whether it has a mortgagee is not known. */
    unread: boolean;
    /** Whether Proof to bank is asked at all: never for auto (Rachy, Oct 28). */
    needed: boolean;
    rows: { label: string; value: string | null }[];
    files: { id: string; fileName: string; kind: string }[];
    /** Umbrella, Flood (part B) and Agency Billed (the agency's invoice) still send an invoice. */
    sendsInvoice: boolean;
    /**
     * The borrower's last name both ways (Rachy, Nov 1; S16): where the row's name comes from, and
     * the other source's name when it differs. Missing from a server from before.
     */
    borrower?: {
      source: 'ams360' | 'dec';
      other: { name: string; source: 'ams360' | 'dec' } | null;
    } | null;
  };
  email: { at: string; by: string | null; templateKey: string } | null;
  close: {
    status: 'done' | 'need_payment';
    /** The carrier or line that renews only once paid ("SageSure", "Umbrella"), or null. */
    payFirst: string | null;
    sheetComment: string;
    renewalStatus: RenewalStatus;
    savedComment: string | null;
  };
};

/** What ticking a step sends (spec C8). */
export type StepTick =
  | { step: 'ams360'; mode: 'match' }
  | { step: 'ams360'; mode: 'accept'; reason: string }
  | {
      step: 'ams360';
      checks: {
        renewClicked: boolean;
        policyNumberSet: boolean;
        coveragesChecked: boolean;
        premiumEntered: boolean;
      };
    }
  | { step: 'email' }
  /** Skip email (S17 extras): no email this time, with the reason. */
  | { step: 'email'; skip: true; reason: string }
  | { step: 'activity'; emailDraggedIn: boolean }
  | {
      step: 'bank';
      outcome: BankOutcome;
      date?: string;
      reason?: string;
      updatedInAms?: boolean;
    }
  | {
      step: 'done';
      status: 'done' | 'need_payment';
      sheetComment: string;
      /** Close anyway: steps are still open, so a reason is required (S16). */
      anyway?: true;
      reason?: string;
    };

/** Step 2 with no new term in AMS360 (S16 Batch 3). */
export type AmsPathAction = 'recheck' | 'recheck_undo' | 'manual' | 'check';

/** Steps 1 to 5 as Mark steps done previously names them; "dec" is step 1 Dec page (S16). */
export type PreviouslyStep = 'dec' | Exclude<StepKey, 'done'>;
/** What Mark steps done previously sends: the steps, who did them and the day (YYYY-MM-DD). */
export type PreviouslyMark = { steps: PreviouslyStep[]; doneBy: string; date: string };

/** Settings, AMS360: the AMS360 web address (Manager, Admin). */
export type AmsAddressView = {
  address: string;
  defaultAddress: string;
  testLink: string | null;
  history: SettingChange[];
};

/** What the renewal sheet check counts in one workbook (the server's SheetCheckCounts, what the page shows). */
export type SheetCounts = {
  year: number | null;
  tabsRead: number;
  /** Visible tabs left out because a column the check needs is missing (a month tab is named). */
  tabsSkipped: { tab: string; missing: string[] }[];
  hiddenTabs: number;
  tabsWithoutTable: number;
  rowsRead: number;
  skippedNoPolicyNumber: number;
  skippedNoDate: number;
  matched: number;
  wouldClose: Record<string, number>;
  skippedPortalWork: number;
  openMatches: Record<string, number>;
  unknownStatus: number;
  conflicts: number;
  conflictRows: number;
  ambiguous: number;
  unmatched: Record<string, number>;
  alreadyClosed?: number;
  reopenedLeftOpen?: number;
  closed?: Record<string, number>;
  closeSkipped?: { notOpen: number; stepTicked: number; reopened: number };
};

/** Settings, Renewal sheets: the dry run of the uploaded workbooks (Manager, Admin). */
export type SheetPreview = {
  /** Null when nothing would close. Works once, for 15 minutes. */
  previewId: string | null;
  expiresAt: string | null;
  closeCount: number;
  counts: SheetCounts[];
};

export type SheetClosed = { runId: number; closed: number; planned: number; counts: SheetCounts[] };

export type Api = {
  me(): Promise<MeResult>;
  /**
   * The server was deployed again since this tab started: its answers carry a newer version than
   * the first one seen. The next page change then loads the whole app again.
   */
  newVersion(): boolean;
  signIn(email: string, password: string): Promise<Result<SignInNext>>;
  verifyCode(code: string, remember: boolean): Promise<Result<SignInNext>>;
  resendCode(): Promise<Result<{ resendIn: number }>>;
  signOut(): Promise<Result<null>>;
  checkLink(token: string): Promise<Result<LinkInfo>>;
  setPassword(token: string, password: string, confirm: string): Promise<Result<SignInNext>>;
  forgot(email: string): Promise<Result<{ sent: true }>>;
  changePassword(
    current: string,
    password: string,
    confirm: string,
  ): Promise<Result<{ changed: true }>>;
  forgetDevice(): Promise<Result<null>>;
  listUsers(): Promise<UserRow[]>;
  addUser(user: NewUser): Promise<Result<{ user: UserRow }>>;
  updateUser(id: string, changes: UserChanges): Promise<Result<{ user: UserRow }>>;
  resendInvitation(id: string): Promise<Result<{ sent: true }>>;
  sendResetLink(id: string): Promise<Result<{ sent: true }>>;
  signOutUser(id: string): Promise<Result<{ sessions: number; devices: number }>>;
  codeRules(): Promise<CodeRulesView>;
  saveCodeRules(rules: CodeRules): Promise<Result<CodeRulesView>>;
  /** The Activity log; without dates, the last 7 days. */
  activity(range?: { from: string; to: string }): Promise<ActivityView>;
  userHistory(id: string): Promise<Result<{ history: HistoryLine[] }>>;
  /** My renewals ("my") or All renewals ("all"). */
  /** The list; `closed` adds the renewals closed on those New York days (the Closed tab, S16). */
  renewals(scope: 'my' | 'all', closed?: { from: string; to: string }): Promise<RenewalsView>;
  /** One renewal for the Record; null when it does not exist or was deleted. */
  renewal(id: string): Promise<RenewalView | null>;
  /** Who renewals can be reassigned to, and whether this person may return them to Unassigned. */
  assignees(): Promise<Assignees>;
  /** Reassign (spec C6): to a Renewal VA, or back to Unassigned (Manager, Admin). */
  reassign(items: ReassignItem[], reason: ReassignReason | null): Promise<Result<ReassignResult>>;
  /** Hand to AE and Reassign to an AE (spec C6): the AE accepts first. */
  createHandoff(renewalId: string, body: HandoffCreate): Promise<Result<HandoffAnswer>>;
  /** Cancel a handoff still waiting (the sender's Undo, Cancel handoff): 409 once answered. */
  cancelHandoff(handoffId: string): Promise<Result<HandoffAnswer>>;
  /** The AE accepts a handoff waiting on them: Handled by becomes the AE, status from the reason. */
  acceptHandoff(handoffId: string): Promise<Result<HandoffAnswer>>;
  /** The AE sends a waiting handoff back to the VA with a required note. */
  sendBackHandoff(handoffId: string, note: string): Promise<Result<HandoffAnswer>>;
  /** Pass to another AE (spec C6), waiting or accepted: they accept first; Handled by stays. */
  passHandoff(handoffId: string, to: string, note: string): Promise<Result<HandoffAnswer>>;
  /**
   * Finish handoff, Return to the VA (spec C6): back to who it came from at `continueAtStep`, with a
   * required outcome note. The steps between where it stopped and that step are stamped by the AE.
   */
  returnHandoff(
    handoffId: string,
    note: string,
    continueAtStep: ContinueAtStep,
  ): Promise<Result<HandoffAnswer>>;
  /**
   * Finish handoff, Close it myself (spec C6): Done or Cancelled with the outcome note and the sheet
   * comment; a rewrite with the bank step returns it to the VA at Proof to bank instead.
   */
  closeHandoff(
    handoffId: string,
    note: string,
    sheetComment: string,
    how: HandoffClose,
  ): Promise<Result<HandoffAnswer>>;
  /** The carriers a rewrite can go to: the ones on the department's renewals. */
  handoffCarriers(): Promise<string[]>;
  /**
   * The toast's Undo after accept, send back or return: the handoff waits for the AE again (is with
   * them again after a return).
   */
  undoHandoffAnswer(handoffId: string): Promise<Result<HandoffAnswer>>;
  /** Follow up on: the AE's day to come back to a handoff with them (YYYY-MM-DD), or null to clear. */
  followUpHandoff(handoffId: string, on: string | null): Promise<Result<FollowUpAnswer>>;
  /** The Follow up on toast's Undo of the change made `at`: the day before comes back. */
  undoFollowUp(handoffId: string, at: string): Promise<Result<FollowUpAnswer>>;
  /** The Record's Portal log: the renewal's own entries, newest first. */
  renewalLog(id: string): Promise<LogEntry[]>;
  /** Sync now (Manager, Admin): 202 started, 409 already running, 503 not set up here. */
  startSync(): Promise<Result<{ started: true }>>;
  /** How the newest sync went, while the list waits for a run to finish. */
  syncStatus(): Promise<SyncInfo>;
  /** The Dec page step's fetch for a renewal. */
  fetchView(renewalId: string): Promise<FetchView>;
  /** Fetch from [Carrier] and Try again: 202 started, 200 already going, 409 not offered. */
  startFetch(renewalId: string): Promise<Result<{ created: boolean; fetch: FetchView }>>;
  /** Confirm or correct a value read from the renewal dec page; answers with the step's view. */
  fixReading(
    renewalId: string,
    fix: ReadingFix,
  ): Promise<Result<{ fixId: string; fetch: FetchView }>>;
  /** The toast's Undo of a confirm or correction, by who made it within 10 minutes. */
  undoFix(renewalId: string, fixId: string): Promise<Result<{ undone: true; fetch: FetchView }>>;
  /** The Files card's Upload PDF (S14): any PDF with its document type. */
  uploadFile(
    renewalId: string,
    file: Blob,
    name: string,
    type: FileType,
  ): Promise<Result<FileAdded>>;
  /** Delete a file added from the Files card. */
  deleteFile(renewalId: string, fileId: string): Promise<Result<{ deleted: true }>>;
  /** The toast's Undo of a delete, by who deleted it within 10 minutes. */
  restoreFile(renewalId: string, fileId: string): Promise<Result<{ restored: true }>>;
  /** Step 1's Delete of a dec page the steps use (S17), while steps 2 to 6 are untouched. */
  deleteDecPage(renewalId: string, docId: string): Promise<Result<{ fetch: FetchView }>>;
  /** The toast's Undo of that Delete, by who deleted it within 10 minutes. */
  restoreDecPage(renewalId: string, docId: string): Promise<Result<{ fetch: FetchView }>>;
  /** My fetches that finished since the server time `since`. */
  myFetches(since: string | null): Promise<MyFetches>;
  /** Where a file on a renewal is read from (same origin, with the session cookie). */
  documentUrl(id: string, download?: boolean): string;
  /** The Email insured step for a renewal, with the template and payment block chosen. */
  emailDraft(
    renewalId: string,
    choice?: { template?: string; payment?: EmailPayment },
  ): Promise<EmailDraft>;
  /** The VA sent the email from Outlook: keep it on the record. */
  markEmailSent(
    renewalId: string,
    email: EmailToSend,
  ): Promise<Result<{ sent: EmailDraft['sent'] }>>;
  /** Settings, Templates (Manager, Admin). */
  templates(): Promise<TemplatesView>;
  saveTemplate(
    key: string,
    t: { to: string; cc: string; subject: string; body: string },
  ): Promise<Result<{ template: EmailTemplateRow }>>;
  resetTemplate(key: string): Promise<Result<{ template: EmailTemplateRow }>>;
  previewTemplate(t: {
    to: string;
    cc: string;
    subject: string;
    body: string;
    flags: Record<string, boolean>;
  }): Promise<Result<TemplatePreview>>;
  templateHistory(key: string): Promise<Result<{ history: SettingChange[] }>>;
  /** The record steps 2 to 6. */
  steps(renewalId: string): Promise<StepsView>;
  /** Tick a step; refused with the reason and the steps as they are. */
  tickStep(renewalId: string, tick: StepTick): Promise<Result<{ steps: StepsView }>>;
  /** Untick the last step ticked (Undo). */
  undoStep(renewalId: string, step: StepKey): Promise<Result<{ steps: StepsView }>>;
  /** Mark steps done previously (S16); `batch` is what the toast's Undo sends back. */
  markStepsPreviously(
    renewalId: string,
    mark: PreviouslyMark,
  ): Promise<Result<{ batch: string; steps: StepsView }>>;
  /** The toast's Undo of Mark steps done previously, by who marked them within 10 minutes. */
  undoStepsPreviously(renewalId: string, batch: string): Promise<Result<{ steps: StepsView }>>;
  /** Step 2 with no new term in AMS360 (S16): Recheck tomorrow and its Undo, renew by hand or back. */
  amsPath(renewalId: string, action: AmsPathAction): Promise<Result<{ steps: StepsView }>>;
  /** Switch to a branch (S17): Non-renewal with its reason, or Policy inactive, and the date. */
  setBranch(
    renewalId: string,
    b: { branch: RenewalBranch; reason?: BranchReason; effectiveDate: string },
  ): Promise<Result<{ steps: StepsView }>>;
  /** Back to renewal steps, with the reason. */
  leaveBranch(renewalId: string, reason: string): Promise<Result<{ steps: StepsView }>>;
  /** The toast's Undo of a switch or of Back, by who did it within 10 minutes. */
  undoBranch(renewalId: string, action: 'set' | 'back'): Promise<Result<{ steps: StepsView }>>;
  tickBranchStep(renewalId: string, tick: BranchTick): Promise<Result<{ steps: StepsView }>>;
  undoBranchStep(renewalId: string, step: BranchStepKey): Promise<Result<{ steps: StepsView }>>;
  /** Refresh from AMS360: read the renewal's new term now; `found` says whether AMS360 had it. */
  refreshAms(renewalId: string): Promise<Result<{ found: boolean; steps: StepsView }>>;
  /** A Need payment renewal is paid: status Done. */
  premiumReceived(renewalId: string): Promise<Result<{ steps: StepsView }>>;
  /** The toast's Undo of Premium received, by the same person within 10 minutes. */
  undoPremiumReceived(renewalId: string): Promise<Result<{ steps: StepsView }>>;
  /** The AE's answer to an increase (S14): Looked over, or I'll reshop it (any AE only). */
  answerIncrease(
    renewalId: string,
    answer: 'looked_over' | 'reshop',
  ): Promise<Result<{ answer: string; previousHandledBy: string | null }>>;
  /** The toast's Undo of that answer, by the same person within 10 minutes. */
  undoIncreaseAnswer(renewalId: string): Promise<Result<{ undone: true }>>;
  /** A renewal's notes, newest first (spec C7.4). */
  notes(renewalId: string): Promise<NotesView>;
  /** Add a note (every role that may open the renewal): 409 deleted when the renewal is read only. */
  addNote(renewalId: string, text: string): Promise<Result<{ note: Note }>>;
  /** The toast's Undo of a note, by its author within 10 minutes. */
  undoNote(renewalId: string, noteId: string): Promise<Result<{ undone: true }>>;
  /** Settings, AMS360: the AMS360 web address (Manager, Admin). */
  amsAddress(): Promise<AmsAddressView>;
  increaseRule(): Promise<IncreaseRuleView>;
  saveIncreaseRule(rule: IncreaseRule): Promise<Result<IncreaseRuleView>>;
  followUpDays(): Promise<FollowUpView>;
  saveFollowUpDays(days: number): Promise<Result<FollowUpView>>;
  saveAmsAddress(address: string): Promise<Result<AmsAddressView>>;
  /** Settings, Renewal sheets: the dry run of one to three workbooks, nothing is closed or kept. */
  sheetPreview(files: File[]): Promise<Result<SheetPreview>>;
  /** Closes exactly what that dry run found (404 preview_gone when it expired or was used). */
  sheetClose(previewId: string): Promise<Result<SheetClosed>>;
  /** Upload PDF: 201 added, 200 already there, 413 too big, 415 not a PDF. */
  uploadDocument(
    renewalId: string,
    file: Blob,
    name: string,
    kind: UploadKind,
  ): Promise<Result<Uploaded>>;
};

type MeBody = {
  user: { id: string; name: string; initials: string; role: RoleCode; email: string };
  sessionEndsAt: string;
  deviceRememberedUntil: string | null;
};

export type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

/**
 * A page's data could not load. The route's error card reads it: 403 is No access, 404 is Page not
 * found, anything else is Server error, with the server's error reference when it gave one.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly ref: string | null;
  constructor(what: string, failure: Failure) {
    super(`${what} failed: ${failure.status} ${failure.error}`);
    this.name = 'ApiError';
    this.status = failure.status;
    this.code = failure.error;
    this.ref = typeof failure.body.ref === 'string' ? failure.body.ref : null;
  }
}

async function readBody(res: Response): Promise<Record<string, unknown>> {
  if (res.status === 204) return {};
  try {
    const body: unknown = await res.json();
    return body && typeof body === 'object' ? (body as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

export function createApi(fetchFn: Fetch = (input, init) => fetch(input, init)): Api {
  // The server's version on the first answer this tab saw, and whether a later one differed.
  let firstVersion: string | undefined;
  let newer = false;
  async function call<T>(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    path: string,
    body?: unknown,
  ): Promise<Result<T>> {
    // The server wants JSON on every state-changing request, DELETE included.
    const sendsJson = method !== 'GET';
    let res: Response;
    try {
      res = await fetchFn(path, {
        method,
        credentials: 'same-origin',
        headers: sendsJson
          ? { 'Content-Type': 'application/json', Accept: 'application/json' }
          : { Accept: 'application/json' },
        body: sendsJson ? JSON.stringify(body ?? {}) : undefined,
      });
    } catch {
      return { ok: false, status: 0, error: 'network', body: {} };
    }
    const version = res.headers?.get?.('x-app-version');
    if (version) {
      firstVersion ??= version;
      if (version !== firstVersion) newer = true;
    }
    const json = await readBody(res);
    if (res.ok) return { ok: true, body: json as T };
    const error = typeof json.error === 'string' ? json.error : 'server_error';
    return { ok: false, status: res.status, error, body: json };
  }

  // Loaders for one navigation run side by side; they share one /api/me request.
  let inFlight: Promise<MeResult> | null = null;

  async function loadMe(): Promise<MeResult> {
    const res = await call<MeBody>('GET', '/api/me');
    if (res.ok) {
      const { user, sessionEndsAt, deviceRememberedUntil } = res.body;
      return {
        kind: 'signed_in',
        me: {
          user: { ...user, role: roleLabels[user.role] },
          sessionEndsAt,
          deviceRememberedUntil,
        },
      };
    }
    if (
      res.status === 401 &&
      res.error === 'session_expired' &&
      typeof res.body.email === 'string'
    ) {
      return { kind: 'expired', email: res.body.email };
    }
    if (res.status === 401) return { kind: 'signed_out' };
    throw new ApiError('GET /api/me', res);
  }

  return {
    newVersion: () => newer,
    me() {
      inFlight ??= loadMe().finally(() => {
        inFlight = null;
      });
      return inFlight;
    },
    signIn: (email, password) => call('POST', '/api/auth/sign-in', { email, password }),
    verifyCode: (code, remember) => call('POST', '/api/auth/code', { code, remember }),
    resendCode: () => call('POST', '/api/auth/code/resend'),
    signOut: () => call('POST', '/api/auth/sign-out'),
    checkLink: (token) => call('POST', '/api/auth/link', { token }),
    setPassword: (token, password, confirm) =>
      call('POST', '/api/auth/set-password', { token, password, confirm }),
    forgot: (email) => call('POST', '/api/auth/forgot', { email }),
    changePassword: (current, password, confirm) =>
      call('POST', '/api/me/password', { current, password, confirm }),
    forgetDevice: () => call('DELETE', '/api/me/device'),
    async listUsers() {
      const res = await call<{ users: UserRow[] }>('GET', '/api/users');
      if (!res.ok) throw new ApiError('GET /api/users', res);
      return res.body.users;
    },
    addUser: (user) => call('POST', '/api/users', user),
    updateUser: (id, changes) => call('PATCH', `/api/users/${encodeURIComponent(id)}`, changes),
    resendInvitation: (id) => call('POST', `/api/users/${encodeURIComponent(id)}/invitation`),
    sendResetLink: (id) => call('POST', `/api/users/${encodeURIComponent(id)}/reset-link`),
    signOutUser: (id) => call('POST', `/api/users/${encodeURIComponent(id)}/sign-out`),
    async codeRules() {
      const res = await call<CodeRulesView>('GET', '/api/settings/sign-in-codes');
      if (!res.ok) throw new ApiError('GET /api/settings/sign-in-codes', res);
      return res.body;
    },
    saveCodeRules: (rules) => call('PUT', '/api/settings/sign-in-codes', { rules }),
    async activity(range) {
      const query = range ? `?${new URLSearchParams(range)}` : '';
      const res = await call<ActivityView>('GET', `/api/activity${query}`);
      if (!res.ok) throw new ApiError('GET /api/activity', res);
      return res.body;
    },
    userHistory: (id) => call('GET', `/api/users/${encodeURIComponent(id)}/history`),
    async renewals(scope, closed) {
      const query = new URLSearchParams({ scope });
      if (closed) {
        query.set('closedFrom', closed.from);
        query.set('closedTo', closed.to);
      }
      const res = await call<RenewalsView>('GET', `/api/renewals?${query}`);
      if (!res.ok) throw new ApiError('GET /api/renewals', res);
      return res.body;
    },
    async renewal(id) {
      const res = await call<RenewalView>('GET', `/api/renewals/${encodeURIComponent(id)}`);
      if (!res.ok && res.status === 404) return null;
      if (!res.ok) throw new ApiError('GET /api/renewals/:id', res);
      return res.body;
    },
    async assignees() {
      const res = await call<Assignees>('GET', '/api/renewals/assignees');
      if (!res.ok) throw new ApiError('GET /api/renewals/assignees', res);
      return res.body;
    },
    reassign: (items, reason) => call('POST', '/api/renewals/reassign', { items, reason }),
    createHandoff: (renewalId, body) =>
      call('POST', `/api/renewals/${encodeURIComponent(renewalId)}/handoffs`, body),
    cancelHandoff: (handoffId) =>
      call('POST', `/api/handoffs/${encodeURIComponent(handoffId)}/cancel`),
    acceptHandoff: (handoffId) =>
      call('POST', `/api/handoffs/${encodeURIComponent(handoffId)}/accept`),
    sendBackHandoff: (handoffId, note) =>
      call('POST', `/api/handoffs/${encodeURIComponent(handoffId)}/send-back`, { note }),
    passHandoff: (handoffId, to, note) =>
      call('POST', `/api/handoffs/${encodeURIComponent(handoffId)}/pass`, { to, note }),
    returnHandoff: (handoffId, note, continueAtStep) =>
      call('POST', `/api/handoffs/${encodeURIComponent(handoffId)}/return`, {
        note,
        continueAtStep,
      }),
    closeHandoff: (handoffId, note, sheetComment, how) =>
      call('POST', `/api/handoffs/${encodeURIComponent(handoffId)}/close`, {
        note,
        sheetComment,
        ...how,
      }),
    async handoffCarriers() {
      const res = await call<{ carriers: string[] }>('GET', '/api/handoffs/carriers');
      return res.ok ? res.body.carriers : [];
    },
    undoHandoffAnswer: (handoffId) =>
      call('POST', `/api/handoffs/${encodeURIComponent(handoffId)}/undo`),
    followUpHandoff: (handoffId, on) =>
      call('POST', `/api/handoffs/${encodeURIComponent(handoffId)}/follow-up`, { on }),
    undoFollowUp: (handoffId, at) =>
      call('POST', `/api/handoffs/${encodeURIComponent(handoffId)}/follow-up/undo`, { at }),
    async renewalLog(id) {
      const res = await call<{ entries: LogEntry[] }>(
        'GET',
        `/api/renewals/${encodeURIComponent(id)}/log`,
      );
      if (!res.ok) throw new ApiError('GET /api/renewals/:id/log', res);
      return res.body.entries;
    },
    startSync: () => call('POST', '/api/sync'),
    async syncStatus() {
      const res = await call<{ sync: SyncInfo }>('GET', '/api/sync');
      if (!res.ok) throw new ApiError('GET /api/sync', res);
      return res.body.sync;
    },
    async fetchView(renewalId) {
      const path = `/api/renewals/${encodeURIComponent(renewalId)}/fetch`;
      const res = await call<{ fetch: FetchView }>('GET', path);
      if (!res.ok) throw new ApiError('GET /api/renewals/:id/fetch', res);
      return res.body.fetch;
    },
    startFetch: (renewalId) => call('POST', `/api/renewals/${encodeURIComponent(renewalId)}/fetch`),
    fixReading: (renewalId, fix) =>
      call('POST', `/api/renewals/${encodeURIComponent(renewalId)}/reading`, fix),
    undoFix: (renewalId, fixId) =>
      call(
        'POST',
        `/api/renewals/${encodeURIComponent(renewalId)}/reading/fixes/${encodeURIComponent(fixId)}/undo`,
      ),
    deleteFile: (renewalId, fileId) =>
      call(
        'POST',
        `/api/renewals/${encodeURIComponent(renewalId)}/files/${encodeURIComponent(fileId)}/delete`,
      ),
    restoreFile: (renewalId, fileId) =>
      call(
        'POST',
        `/api/renewals/${encodeURIComponent(renewalId)}/files/${encodeURIComponent(fileId)}/restore`,
      ),
    deleteDecPage: (renewalId, docId) =>
      call(
        'POST',
        `/api/renewals/${encodeURIComponent(renewalId)}/dec-pages/${encodeURIComponent(docId)}/delete`,
      ),
    restoreDecPage: (renewalId, docId) =>
      call(
        'POST',
        `/api/renewals/${encodeURIComponent(renewalId)}/dec-pages/${encodeURIComponent(docId)}/restore`,
      ),
    async myFetches(since) {
      const query = since ? `?${new URLSearchParams({ since })}` : '';
      const res = await call<MyFetches>('GET', `/api/fetches/mine${query}`);
      if (!res.ok) throw new ApiError('GET /api/fetches/mine', res);
      return res.body;
    },
    documentUrl: (id, download = false) =>
      `/api/documents/${encodeURIComponent(id)}${download ? '?download=1' : ''}`,
    async emailDraft(renewalId, choice = {}) {
      const q = new URLSearchParams();
      if (choice.template) q.set('template', choice.template);
      if (choice.payment) q.set('payment', choice.payment);
      const query = q.size ? `?${q}` : '';
      const path = `/api/renewals/${encodeURIComponent(renewalId)}/email${query}`;
      const res = await call<{ email: EmailDraft }>('GET', path);
      if (!res.ok) throw new ApiError('GET /api/renewals/:id/email', res);
      return res.body.email;
    },
    markEmailSent: (renewalId, email) =>
      call('POST', `/api/renewals/${encodeURIComponent(renewalId)}/email/sent`, email),
    async templates() {
      const res = await call<TemplatesView>('GET', '/api/settings/templates');
      if (!res.ok) throw new ApiError('GET /api/settings/templates', res);
      return res.body;
    },
    saveTemplate: (key, t) => call('PUT', `/api/settings/templates/${encodeURIComponent(key)}`, t),
    resetTemplate: (key) => call('DELETE', `/api/settings/templates/${encodeURIComponent(key)}`),
    previewTemplate: (t) => call('POST', '/api/settings/templates/preview', t),
    templateHistory: (key) =>
      call('GET', `/api/settings/templates/${encodeURIComponent(key)}/history`),
    async steps(renewalId) {
      const path = `/api/renewals/${encodeURIComponent(renewalId)}/steps`;
      const res = await call<{ steps: StepsView }>('GET', path);
      if (!res.ok) throw new ApiError('GET /api/renewals/:id/steps', res);
      return res.body.steps;
    },
    tickStep: (renewalId, { step, ...body }) =>
      call(
        'POST',
        `/api/renewals/${encodeURIComponent(renewalId)}/steps/${encodeURIComponent(step)}`,
        body,
      ),
    undoStep: (renewalId, step) =>
      call(
        'DELETE',
        `/api/renewals/${encodeURIComponent(renewalId)}/steps/${encodeURIComponent(step)}`,
      ),
    markStepsPreviously: (renewalId, mark) =>
      call('POST', `/api/renewals/${encodeURIComponent(renewalId)}/steps-previously`, mark),
    undoStepsPreviously: (renewalId, batch) =>
      call('POST', `/api/renewals/${encodeURIComponent(renewalId)}/steps-previously/undo`, {
        batch,
      }),
    amsPath: (renewalId, action) =>
      call('POST', `/api/renewals/${encodeURIComponent(renewalId)}/ams-path`, { action }),
    setBranch: (renewalId, b) =>
      call('POST', `/api/renewals/${encodeURIComponent(renewalId)}/branch`, b),
    leaveBranch: (renewalId, reason) =>
      call('POST', `/api/renewals/${encodeURIComponent(renewalId)}/branch/back`, { reason }),
    undoBranch: (renewalId, action) =>
      call('POST', `/api/renewals/${encodeURIComponent(renewalId)}/branch/undo`, { action }),
    tickBranchStep: (renewalId, { step, ...body }) =>
      call(
        'POST',
        `/api/renewals/${encodeURIComponent(renewalId)}/branch-steps/${encodeURIComponent(step)}`,
        body,
      ),
    undoBranchStep: (renewalId, step) =>
      call(
        'DELETE',
        `/api/renewals/${encodeURIComponent(renewalId)}/branch-steps/${encodeURIComponent(step)}`,
      ),
    refreshAms: (renewalId) =>
      call('POST', `/api/renewals/${encodeURIComponent(renewalId)}/ams-refresh`),
    premiumReceived: (renewalId) =>
      call('POST', `/api/renewals/${encodeURIComponent(renewalId)}/premium-received`),
    undoPremiumReceived: (renewalId) =>
      call('POST', `/api/renewals/${encodeURIComponent(renewalId)}/premium-received/undo`),
    answerIncrease: (renewalId, answer) =>
      call('POST', `/api/renewals/${encodeURIComponent(renewalId)}/increase-review`, { answer }),
    undoIncreaseAnswer: (renewalId) =>
      call('POST', `/api/renewals/${encodeURIComponent(renewalId)}/increase-review/undo`),
    async notes(renewalId) {
      const path = `/api/renewals/${encodeURIComponent(renewalId)}/notes`;
      const res = await call<NotesView>('GET', path);
      if (!res.ok) throw new ApiError(`GET ${path}`, res);
      return res.body;
    },
    addNote: (renewalId, text) =>
      call('POST', `/api/renewals/${encodeURIComponent(renewalId)}/notes`, { text }),
    undoNote: (renewalId, noteId) =>
      call(
        'POST',
        `/api/renewals/${encodeURIComponent(renewalId)}/notes/${encodeURIComponent(noteId)}/undo`,
      ),
    async amsAddress() {
      const res = await call<AmsAddressView>('GET', '/api/settings/ams360-address');
      if (!res.ok) throw new ApiError('GET /api/settings/ams360-address', res);
      return res.body;
    },
    saveAmsAddress: (address) => call('PUT', '/api/settings/ams360-address', { address }),
    async increaseRule() {
      const res = await call<IncreaseRuleView>('GET', '/api/settings/increase-rule');
      if (!res.ok) throw new ApiError('GET /api/settings/increase-rule', res);
      return res.body;
    },
    saveIncreaseRule: (rule) => call('PUT', '/api/settings/increase-rule', { rule }),
    async followUpDays() {
      const res = await call<FollowUpView>('GET', '/api/settings/follow-up-days');
      if (!res.ok) throw new ApiError('GET /api/settings/follow-up-days', res);
      return res.body;
    },
    saveFollowUpDays: (days) => call('PUT', '/api/settings/follow-up-days', { days }),
    uploadDocument: (renewalId, file, name, kind) =>
      sendPdf<Uploaded>(
        `/api/renewals/${encodeURIComponent(renewalId)}/documents?kind=${kind}`,
        file,
        name,
      ),
    sheetPreview: (files) => sendSheets(files),
    sheetClose: (previewId) => call('POST', '/api/sheets/close', { previewId }),
    uploadFile: (renewalId, file, name, type) =>
      sendPdf<FileAdded>(
        `/api/renewals/${encodeURIComponent(renewalId)}/files?type=${type}`,
        file,
        name,
      ),
  };

  /** The workbooks as a form; the custom header is what the server asks of this one upload. */
  async function sendSheets(files: File[]): Promise<Result<SheetPreview>> {
    const form = new FormData();
    for (const f of files) form.append('files', f);
    let res: Response;
    try {
      res = await fetchFn('/api/sheets/preview', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'X-Sheet-Upload': '1', Accept: 'application/json' },
        body: form,
      });
    } catch {
      return { ok: false, status: 0, error: 'network', body: {} };
    }
    const json = await readBody(res);
    if (res.ok) return { ok: true, body: json as SheetPreview };
    const error = typeof json.error === 'string' ? json.error : 'server_error';
    return { ok: false, status: res.status, error, body: json };
  }

  /** A PDF as the request body, its name in a header (Upload PDF in step 1 and on the Files card). */
  async function sendPdf<T>(path: string, file: Blob, name: string): Promise<Result<T>> {
    let res: Response;
    try {
      // The file itself is the body; its name goes in a header, encoded.
      res = await fetchFn(path, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/pdf',
          'X-File-Name': encodeURIComponent(name),
          Accept: 'application/json',
        },
        body: file,
      });
    } catch {
      return { ok: false, status: 0, error: 'network', body: {} };
    }
    const json = await readBody(res);
    if (res.ok) return { ok: true, body: json as T };
    const error = typeof json.error === 'string' ? json.error : 'server_error';
    return { ok: false, status: res.status, error, body: json };
  }
}
